import { devAuthUser, env } from '@config/env';
import { recordRestInstance } from './serverInstance';
import { parseSse, type SseEvent } from './sse';

export interface ApiClientOptions {
  getAccessToken: () => Promise<string | null>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
  ) {
    // Prefer the server's ProblemDetails message (RFC 7807 `detail`, then `title`) so the UI shows
    // the real reason — e.g. "Commission cannot be negative." — instead of a generic
    // "API error 400: Bad Request". Falls back to status text when the body has no message.
    super(ApiError.messageFrom(body) ?? `API error ${status}: ${statusText}`);
  }

  private static messageFrom(body: unknown): string | null {
    if (body && typeof body === 'object') {
      const p = body as { detail?: unknown; title?: unknown };
      if (typeof p.detail === 'string' && p.detail.trim()) return p.detail;
      if (typeof p.title === 'string' && p.title.trim()) return p.title;
    }
    return null;
  }
}

/** Request exceeded its client-side timeout (our AbortController fired). Distinguishable via `kind`. */
export class ApiTimeoutError extends Error {
  readonly kind = 'timeout' as const;
  constructor(
    public readonly url: string,
    public readonly timeoutMs: number,
  ) {
    super(`Request to ${url} timed out after ${timeoutMs}ms`);
    this.name = 'ApiTimeoutError';
  }
}

/** Transport failure (CORS / DNS / offline / connection reset) — fetch rejected with no HTTP response. */
export class NetworkError extends Error {
  readonly kind = 'network' as const;
  constructor(
    public readonly url: string,
    cause: unknown,
  ) {
    super('Could not reach the server. Check your connection and try again.', { cause });
    this.name = 'NetworkError';
  }
}

/** Default per-request timeout for JSON calls (ms). Long enough for a warm backend, short enough that a
 * hung/unreachable backend fails fast instead of leaving the UI spinning forever. */
export const DEFAULT_TIMEOUT_MS = 20_000;
/** Longer timeout for multipart uploads and slow endpoints that opt in (e.g. the AI /parse LLM call). */
export const UPLOAD_TIMEOUT_MS = 60_000;

// A Symbol so no external AbortSignal reason can ever collide with (and be misclassified as) our timeout.
const TIMEOUT_REASON = Symbol('api-timeout');

/**
 * RequestInit plus an optional per-request timeout override (ms) and, for a request that lands under the
 * server's AI capacity policy (the Dashboard recap, 2026-10-29), the opt-in to the busy rule `postStream`
 * applies by default: one wait and one retry on a `429` or no response, then `AiBusyError`. Every other
 * request keeps its single attempt.
 */
export type ApiRequestInit = RequestInit & { timeoutMs?: number; retryWhenBusy?: boolean };

/** True when an error is a 403 whose ProblemDetails `type` ends with an approval-gate code. */
export function isApprovalError(err: unknown): 'pending' | 'rejected' | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  const body = err.body as { type?: unknown } | null;
  const type = body && typeof body === 'object' && typeof body.type === 'string' ? body.type : '';
  if (type.endsWith('users.pending_approval')) return 'pending';
  if (type.endsWith('users.rejected')) return 'rejected';
  return null;
}

/** True when an error is a 403 whose ProblemDetails `type` ends with a subscription-gate code. No
 * `trialExpired` variant — the 7-day trial was removed 2026-08-13 in favor of a free base tier, so
 * every unentitled user (paid-and-lapsed or otherwise) surfaces as `subscriptions.inactive`. */
export function isSubscriptionError(err: unknown): 'inactive' | 'tierRequired' | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  const body = err.body as { type?: unknown } | null;
  const type = body && typeof body === 'object' && typeof body.type === 'string' ? body.type : '';
  if (type.endsWith('subscriptions.inactive')) return 'inactive';
  if (type.endsWith('subscriptions.tier_required')) return 'tierRequired';
  return null;
}

/** The server closed a streamed reply before the final event (2026-09-02). The BE never charges such a turn. */
export class StreamEndedError extends Error {
  readonly kind = 'stream-ended' as const;
  constructor() {
    super('The connection dropped before the answer arrived. You were not charged.');
    this.name = 'StreamEndedError';
  }
}

/**
 * The AI endpoint had no capacity for the message (2026-10-29, QA item 8): the server answered `429`
 * before any stream byte, or fetch itself rejected before a response, and once more after the wait and
 * the single retry. Nothing was sent to the model and nothing was charged. The message is the one the AI
 * chats show, word for word.
 */
export class AiBusyError extends Error {
  readonly kind = 'ai-busy' as const;
  constructor() {
    super('AI Assist is busy right now. Your message was not sent and you were not charged.');
    this.name = 'AiBusyError';
  }
}

/**
 * The reply broke off after the server had taken the message (QA item 8 follow-up, 2026-11-02): the
 * response had started (its headers, or some of its bytes, had arrived) and then the connection failed.
 * That is our side dropping the turn (a task that died, a deploy or a scale-in that cut it), not the
 * trader's connection, so the line never says to check it. The message is the one the AI chats show,
 * word for word, beside a `Try again`. Nothing is resent on its own: the first attempt may still be
 * running on a surviving task, and the server charges a turn only when its reply was saved.
 */
export class StreamDroppedError extends Error {
  readonly kind = 'stream-dropped' as const;
  constructor(
    public readonly url: string,
    cause: unknown,
  ) {
    super('The reply was interrupted on our side. Please try again.', { cause });
    this.name = 'StreamDroppedError';
  }
}

/**
 * Neither the attempt nor the single retry got any response at all (QA item 8 follow-up, 2026-11-02):
 * the AI endpoint cannot be reached (a dead target behind the load balancer, an outage), which is not
 * "busy": a `429` on either attempt stays `AiBusyError`. Nothing was sent to the model and nothing was
 * charged. The message is the one the AI chats show, word for word.
 */
export class AiUnreachableError extends Error {
  readonly kind = 'ai-unreachable' as const;
  constructor() {
    super("AI Assist can't be reached right now. Please try again in a minute.");
    this.name = 'AiUnreachableError';
  }
}

/**
 * The failures the AI chats answer with a `Try again` beside the line, which resends the message exactly
 * as it was sent: the busy refusal (owner decision 2026-10-29), the unreachable endpoint and the reply
 * that broke off (QA item 8 follow-up, 2026-11-02). Every other failure keeps its line alone.
 */
export function isRetryableAiFailure(err: unknown): err is AiBusyError | AiUnreachableError | StreamDroppedError {
  return err instanceof AiBusyError || err instanceof AiUnreachableError || err instanceof StreamDroppedError;
}

/** The wait before the one retry of a busy AI stream when `Retry-After` is missing or unreadable, in seconds. */
export const AI_BUSY_RETRY_DEFAULT_SEC = 5;
/** The longest wait before that retry, whatever `Retry-After` says, in seconds. */
export const AI_BUSY_RETRY_MAX_SEC = 10;

/**
 * The wait a `Retry-After` header asks for, in seconds: the default (5) when it is missing or not a
 * number of seconds (an HTTP date is not read), never more than `AI_BUSY_RETRY_MAX_SEC`.
 */
export function retryAfterSeconds(header: string | null): number {
  const seconds = header === null || header.trim() === '' ? NaN : Number(header);
  if (!Number.isFinite(seconds) || seconds < 0) return AI_BUSY_RETRY_DEFAULT_SEC;
  return Math.min(seconds, AI_BUSY_RETRY_MAX_SEC);
}

/** Resolves after `ms`, or rejects with the signal's reason the moment it aborts. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(signal.reason); return; }
    const onAbort = () => { clearTimeout(timer); reject(signal.reason); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', onAbort); resolve(); }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * One attempt at a request: the response, or the transport failure that gave none. An attempt THROWS
 * (its timeout error, or the caller's own abort) when there is nothing to retry.
 */
type Attempt = () => Promise<Response | NetworkError>;

/** The two shapes the server's AI capacity policy produces before any work: no response, or a `429`. */
function isBusy(outcome: Response | NetworkError): boolean {
  return outcome instanceof NetworkError || outcome.status === 429;
}

/**
 * What else the busy rule takes: `beforeRetry` runs just before the second attempt (`postStream` re-arms
 * its idle timer); `unreachable` is thrown instead of `AiBusyError` when NEITHER attempt got any response
 * (the AI chats, QA item 8 follow-up 2026-11-02). Without it two silences read as busy, as they did.
 */
interface BusyRuleOptions {
  beforeRetry?: () => void;
  unreachable?: () => Error;
}

/**
 * The busy rule (owner decision 2026-10-29, QA item 8), shared by `postStream` and by a `send` that opts
 * in (`retryWhenBusy`, the Dashboard recap): ONE attempt; when it gives a `429` or no response at all,
 * wait `Retry-After` seconds (5 by default, 10 at most) and try ONCE more; the same again is
 * `AiBusyError`, or `options.unreachable()` when neither attempt got any response (a dead target, an
 * outage: nothing answered, so it is not load the server refused). Any other outcome of either attempt
 * is returned as it is, for the caller to read as usual. The wait honours `signal`: an abort during it
 * rejects with the signal's reason, and nothing is retried.
 */
async function openUnlessBusy(attempt: Attempt, signal: AbortSignal, options: BusyRuleOptions = {}): Promise<Response> {
  const first = await attempt();
  if (!isBusy(first)) return first as Response;
  const seconds = first instanceof NetworkError
    ? AI_BUSY_RETRY_DEFAULT_SEC
    : retryAfterSeconds(first.headers.get('retry-after'));
  await pause(seconds * 1000, signal);
  options.beforeRetry?.();
  const second = await attempt();
  if (!isBusy(second)) return second as Response;
  if (first instanceof NetworkError && second instanceof NetworkError && options.unreachable) throw options.unreachable();
  throw new AiBusyError();
}

export const DEV_USER_HEADER = 'X-Dev-User';

export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  async get<T>(path: string, init?: ApiRequestInit): Promise<T> {
    return this.send<T>('GET', path, undefined, init);
  }

  async post<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
    return this.send<T>('POST', path, body, init);
  }

  async put<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
    return this.send<T>('PUT', path, body, init);
  }

  async patch<T>(path: string, body?: unknown, init?: ApiRequestInit): Promise<T> {
    return this.send<T>('PATCH', path, body, init);
  }

  async delete<T = void>(path: string, init?: ApiRequestInit): Promise<T> {
    return this.send<T>('DELETE', path, undefined, init);
  }

  /** Multipart form upload — caller builds the FormData. Auth header is injected like any other request. */
  async postForm<T>(path: string, form: FormData): Promise<T> {
    const url = new URL(path, env.apiBaseUrl).toString();
    const headers = await this.buildAuthHeaders(false);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(TIMEOUT_REASON), UPLOAD_TIMEOUT_MS);
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST', headers, body: form, signal: controller.signal,
        // See send() — carry ALB's stickiness cookie so relay POST + hub land on the same task.
        credentials: 'include',
      });
    } catch (err) {
      if (controller.signal.reason === TIMEOUT_REASON) throw new ApiTimeoutError(url, UPLOAD_TIMEOUT_MS);
      throw new NetworkError(url, err);
    } finally {
      clearTimeout(timer);
    }

    if (response.status === 204) return undefined as T;
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('json') ? await response.json() : await response.text();
    if (!response.ok) throw new ApiError(response.status, response.statusText, payload);
    return payload as T;
  }

  /**
   * Streamed POST (2026-09-02, the AI chats): `Accept: text/event-stream`, and every SSE event is
   * handed to `onEvent` the moment it is parsed; the caller picks the events it reads (the AI chats
   * read only `reply` and `error`, and no reasoning is sent since 2026-09-25). A JSON body (the server chose not to
   * stream) is surfaced as ONE synthetic `reply` event so callers have a single code path. The
   * timeout is an IDLE timeout — no bytes for `idleTimeoutMs` — because a live turn can legitimately
   * run for a minute, but never goes silent (the BE heartbeats every 15s).
   *
   * Busy (2026-10-29, QA item 8, `openUnlessBusy`): a `429` before any stream byte (the server sends
   * `Retry-After: 5`) or a fetch that rejects before any response waits `Retry-After` seconds (5 by
   * default, 10 at most) and tries ONCE more; the same again rejects with `AiBusyError`, or with
   * `AiUnreachableError` when neither attempt got any response at all (QA item 8 follow-up, 2026-11-02:
   * nothing answered, so it is not load). Nothing is retried once a response has arrived: the model may
   * be at work on the first attempt, and the BE may charge for it. A connection that fails AFTER the
   * response started is `StreamDroppedError`, our side dropping the turn, never a `NetworkError` that
   * would tell the trader to check their own connection.
   */
  async postStream(
    path: string,
    body: unknown,
    onEvent: (event: SseEvent) => void,
    init: { signal?: AbortSignal; idleTimeoutMs?: number } = {},
  ): Promise<void> {
    const url = new URL(path, env.apiBaseUrl).toString();
    const headers = await this.buildAuthHeaders(true);
    headers.set('Accept', 'text/event-stream');
    const { idleTimeoutMs = 45_000, signal: externalSignal } = init;

    const controller = new AbortController();
    let timer = setTimeout(() => controller.abort(TIMEOUT_REASON), idleTimeoutMs);
    const armIdle = () => {
      clearTimeout(timer);
      timer = setTimeout(() => controller.abort(TIMEOUT_REASON), idleTimeoutMs);
    };
    const onExternalAbort = () => controller.abort(externalSignal?.reason);
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort(externalSignal.reason);
      else externalSignal.addEventListener('abort', onExternalAbort, { once: true });
    }

    try {
      /** One attempt at the request: the response, or the transport failure that gave none. */
      const open = async (): Promise<Response | NetworkError> => {
        try {
          return await fetch(url, {
            method: 'POST', headers, body: JSON.stringify(body), signal: controller.signal,
            credentials: 'include', // the ALB stickiness cookie, as in send()
          });
        } catch (err) {
          if (controller.signal.reason === TIMEOUT_REASON) throw new ApiTimeoutError(url, idleTimeoutMs);
          if (externalSignal?.aborted) throw err;
          return new NetworkError(url, err);
        }
      };
      let response: Response;
      try {
        // A 429 before any stream byte: the server had no room for the message. No response at all, twice:
        // the endpoint cannot be reached, which the chats say as such, not as "busy".
        response = await openUnlessBusy(open, controller.signal, {
          beforeRetry: armIdle,
          unreachable: () => new AiUnreachableError(),
        });
      } catch (err) {
        if (controller.signal.reason === TIMEOUT_REASON) throw new ApiTimeoutError(url, idleTimeoutMs);
        throw err; // the caller's own abort (Cancel, a closed dialog), or the busy refusal itself
      }
      recordRestInstance(response.headers.get('x-instance-id'), path);

      const contentType = response.headers.get('content-type') ?? '';
      if (!response.ok) {
        const payload = contentType.includes('json') ? await response.json() : await response.text();
        throw new ApiError(response.status, response.statusText, payload);
      }
      if (!contentType.includes('text/event-stream')) {
        onEvent({ event: 'reply', data: await response.text() });
        return;
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          armIdle();
          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSse(buffer);
          buffer = parsed.rest;
          for (const e of parsed.events) onEvent(e);
        }
      } catch (err) {
        if (controller.signal.reason === TIMEOUT_REASON) throw new ApiTimeoutError(url, idleTimeoutMs);
        if (externalSignal?.aborted) throw err;
        // The server had taken the message and begun to answer: a break here is our side dropping the
        // turn (QA item 8 follow-up, 2026-11-02), never the trader's connection.
        throw new StreamDroppedError(url, err);
      }
    } finally {
      clearTimeout(timer);
      externalSignal?.removeEventListener('abort', onExternalAbort);
    }
  }

  private async send<T>(method: string, path: string, body: unknown, init?: ApiRequestInit): Promise<T> {
    const url = new URL(path, env.apiBaseUrl).toString();
    const headers = await this.buildAuthHeaders(body !== undefined, init?.headers);
    const { timeoutMs = DEFAULT_TIMEOUT_MS, signal: externalSignal, retryWhenBusy = false, ...rest } = init ?? {};

    // Our own timeout via AbortController — a hung/unreachable backend must fail fast, not spin forever
    // (bare fetch has no timeout; that's what stranded /me on "Signing you in…" in the 2026-07 incident).
    // An externally supplied signal (e.g. React Query unmount cancellation) is honoured too.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(TIMEOUT_REASON), timeoutMs);
    const onExternalAbort = () => controller.abort(externalSignal?.reason);
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort(externalSignal.reason);
      else externalSignal.addEventListener('abort', onExternalAbort, { once: true });
    }

    /** One attempt at the request: the response, or the transport failure that gave none. */
    const attempt = async (): Promise<Response | NetworkError> => {
      try {
        return await fetch(url, {
          ...rest,
          method,
          headers,
          body: body === undefined ? undefined : JSON.stringify(body),
          signal: controller.signal,
          // Send + receive the ALB stickiness cookie (2026-07-30). The CQG/Rithmic relay session lives
          // in ONE API task's memory, so the connect POST, the /status polls and the hub WebSocket must
          // all land on the SAME task — otherwise the browser's login frames reach a task with no
          // session and a rejected/accepted logon is silently dropped (the "connecting forever" bug).
          // ALB stickiness already emits an AWSALBCORS cookie and CORS is explicit-origin +
          // AllowCredentials; the ONLY missing link was the browser opting in to carry it. This is a
          // routing cookie ONLY — an opaque "which task" token the ALB issues. No user secret, and
          // ESPECIALLY not the broker password, ever goes in a cookie: broker creds stay in localStorage
          // and ride only the browser's own WebSocket to Rithmic/CQG, never a request to our BE/ALB.
          credentials: 'include',
        });
      } catch (err) {
        if (controller.signal.reason === TIMEOUT_REASON) throw new ApiTimeoutError(url, timeoutMs);
        if (externalSignal?.aborted) throw err; // genuine caller cancellation — let React Query treat it as a cancel
        return new NetworkError(url, err);
      }
    };

    let response: Response;
    try {
      if (retryWhenBusy) {
        // The AI capacity policy's busy rule (2026-10-29, the Dashboard recap): one wait, one retry, then
        // `AiBusyError`, two silences included: the recap keeps the owner's recap wording of 2026-10-29
        // (the chats' unreachable line, QA item 8 follow-up 2026-11-02, is not applied to it). The wait
        // counts against this request's own timeout.
        response = await openUnlessBusy(attempt, controller.signal);
      } else {
        const outcome = await attempt();
        if (outcome instanceof NetworkError) throw outcome;
        response = outcome;
      }
    } catch (err) {
      // The timeout can also fire during the busy wait: it reads as the timeout either way.
      if (controller.signal.reason === TIMEOUT_REASON) throw new ApiTimeoutError(url, timeoutMs);
      throw err;
    } finally {
      clearTimeout(timer);
      externalSignal?.removeEventListener('abort', onExternalAbort);
    }

    // Which API task served this? Relay sessions are per-task memory, so seeing >1 task across a page
    // load explains a broken CQG/Rithmic connect (see core/api/serverInstance.ts).
    recordRestInstance(response.headers.get('x-instance-id'), path);

    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('json') ? await response.json() : await response.text();

    if (!response.ok) {
      throw new ApiError(response.status, response.statusText, payload);
    }

    return payload as T;
  }

  private async buildAuthHeaders(hasJsonBody: boolean, initHeaders?: HeadersInit): Promise<Headers> {
    const headers = new Headers(initHeaders);
    headers.set('Accept', 'application/json');
    if (hasJsonBody) headers.set('Content-Type', 'application/json');

    if (devAuthUser) {
      headers.set(DEV_USER_HEADER, devAuthUser);
    } else {
      const token = await this.opts.getAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
}
